import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { fetchAllRecommendations } from "@/v2/services/decisionEngine";

/*
  DecisionContext — single global source of truth for recommendations.
  - In-memory cache
  - Background refresh every 60 seconds
  - Dismiss / Pin / Bookmark actions persisted in localStorage
*/

const DecisionContext = createContext(null);

const LS_KEYS = {
  dismissed: "zj_v2_dec_dismissed",
  pinned: "zj_v2_dec_pinned",
  bookmarks: "zj_v2_dec_bookmarks",
  assigned: "zj_v2_dec_assigned",
  comments: "zj_v2_dec_comments",
};

function loadJSON(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}
function saveJSON(key, v) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore quota */ }
}

export function DecisionProvider({ children }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [dismissed, setDismissed] = useState(() => loadJSON(LS_KEYS.dismissed, {}));
  const [pinned, setPinned] = useState(() => loadJSON(LS_KEYS.pinned, {}));
  const [bookmarks, setBookmarks] = useState(() => loadJSON(LS_KEYS.bookmarks, {}));
  const [assigned, setAssigned] = useState(() => loadJSON(LS_KEYS.assigned, {}));
  const [comments, setComments] = useState(() => loadJSON(LS_KEYS.comments, {}));
  const inflight = useRef(false);

  const refresh = useCallback(async (force = false) => {
    if (inflight.current && !force) return;
    inflight.current = true;
    setLoading(true);
    try {
      const recs = await fetchAllRecommendations();
      setRecommendations(recs);
      setLastLoadedAt(Date.now());
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, []);

  // Initial + 60s background refresh
  useEffect(() => {
    refresh();
    const t = setInterval(() => refresh(), 60000);
    return () => clearInterval(t);
  }, [refresh]);

  const dismiss = useCallback((id) => {
    setDismissed((prev) => {
      const next = { ...prev, [id]: Date.now() };
      saveJSON(LS_KEYS.dismissed, next);
      return next;
    });
  }, []);

  const undismiss = useCallback((id) => {
    setDismissed((prev) => {
      const next = { ...prev };
      delete next[id];
      saveJSON(LS_KEYS.dismissed, next);
      return next;
    });
  }, []);

  const togglePin = useCallback((id) => {
    setPinned((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = Date.now();
      saveJSON(LS_KEYS.pinned, next);
      return next;
    });
  }, []);

  const toggleBookmark = useCallback((id) => {
    setBookmarks((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = Date.now();
      saveJSON(LS_KEYS.bookmarks, next);
      return next;
    });
  }, []);

  const assign = useCallback((id, user) => {
    setAssigned((prev) => {
      const next = { ...prev, [id]: user };
      saveJSON(LS_KEYS.assigned, next);
      return next;
    });
  }, []);

  const addComment = useCallback((id, comment) => {
    setComments((prev) => {
      const list = prev[id] || [];
      const next = { ...prev, [id]: [...list, { ...comment, at: new Date().toISOString() }] };
      saveJSON(LS_KEYS.comments, next);
      return next;
    });
  }, []);

  // Enrich recommendations with runtime state
  const enriched = recommendations.map((r) => ({
    ...r,
    isPinned: !!pinned[r.id],
    isBookmarked: !!bookmarks[r.id],
    isDismissed: !!dismissed[r.id],
    assignee: assigned[r.id] || null,
    comments: comments[r.id] || [],
  }));

  const value = {
    recommendations: enriched,
    loading,
    lastLoadedAt,
    refresh,
    dismiss,
    undismiss,
    togglePin,
    toggleBookmark,
    assign,
    addComment,
    // helpers
    getById: (id) => enriched.find((r) => r.id === id),
    getByCategory: (cat) => enriched.filter((r) => r.category === cat && !r.isDismissed),
    getActive: () => enriched.filter((r) => !r.isDismissed),
    getPinned: () => enriched.filter((r) => r.isPinned),
    getBookmarked: () => enriched.filter((r) => r.isBookmarked),
    stats: {
      total: enriched.length,
      active: enriched.filter((r) => !r.isDismissed).length,
      urgent: enriched.filter((r) => r.priority === "urgent" && !r.isDismissed).length,
      high: enriched.filter((r) => r.priority === "high" && !r.isDismissed).length,
      pinned: enriched.filter((r) => r.isPinned).length,
      totalRevenueUpside: enriched.reduce((s, r) => s + (r.estimatedRevenueGain || 0), 0),
      totalSavings: enriched.reduce((s, r) => s + (r.estimatedSavings || 0), 0),
    },
  };

  return <DecisionContext.Provider value={value}>{children}</DecisionContext.Provider>;
}

export function useDecisionEngine() {
  const ctx = useContext(DecisionContext);
  if (!ctx) throw new Error("useDecisionEngine must be used within DecisionProvider");
  return ctx;
}

export function useRecommendations(filter = {}) {
  const { recommendations } = useDecisionEngine();
  return recommendations.filter((r) => {
    if (r.isDismissed) return false;
    if (filter.category && filter.category !== "all" && r.category !== filter.category) return false;
    if (filter.priority && filter.priority !== "all" && r.priority !== filter.priority) return false;
    if (filter.minConfidence && r.confidence < filter.minConfidence) return false;
    if (filter.pinnedOnly && !r.isPinned) return false;
    if (filter.bookmarkedOnly && !r.isBookmarked) return false;
    return true;
  });
}
